import React from 'react';
import Badge from './Badge';

export interface StatusBadgeProps {
  status: string;
  variant?: 'success' | 'warning' | 'error' | 'info' | 'neutral';
  size?: 'sm' | 'md' | 'lg';
  fullCell?: boolean;
  domain?: 'status' | 'wineType';
  className?: string;
}

/**
 * Smart status badge component that automatically maps status strings to colors
 * based on common status values. Can also accept explicit variant override.
 */
const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  variant,
  size = 'md',
  fullCell = false,
  domain = 'status',
  className = '',
}) => {
  // Auto-detect variant from status string if not explicitly provided
  const getVariant = (): 'success' | 'warning' | 'error' | 'info' | 'neutral' => {
    if (variant) return variant;
    
    const normalizedStatus = (status || '').toLowerCase().trim();

    if (domain === 'wineType') {
      if (['red', 'cabernet sauvignon', 'merlot', 'syrah'].includes(normalizedStatus)) {
        return 'error';
      }
      if (['white', 'chardonnay', 'sauvignon blanc'].includes(normalizedStatus)) {
        return 'warning';
      }
      if (['rosé', 'rose'].includes(normalizedStatus)) {
        return 'info';
      }
      return 'neutral';
    }
    
    // Success states
    if (['completed', 'active', 'done', 'approved', 'finished', 'on track'].includes(normalizedStatus)) {
      return 'success';
    }
    
    // In progress states
    if (['in progress', 'in use', 'running', 'fermenting', 'aging', 'processing', 'shipping', 'final testing'].includes(normalizedStatus)) {
      return 'info';
    }
    
    // Warning/Pending states
    if (['pending', 'planned', 'waiting', 'on hold', 'scheduled', 'empty'].includes(normalizedStatus)) {
      return 'warning';
    }
    
    // Error states
    if (['error', 'stuck', 'failed', 'cancelled', 'rejected', 'inactive', 'out of service', 'stopped'].includes(normalizedStatus)) {
      return 'error';
    }
    
    // Default
    return 'neutral';
  };

  return (
    <Badge
      variant={getVariant()}
      size={size}
      fullCell={fullCell}
      className={className}
    >
      {status}
    </Badge>
  );
};

export default StatusBadge;
